import * as L from 'leaflet'
import {OBJECT} from './types.js'
import PopupCreator from './PopupCreator.js'
import MarkerCreator from './MarkerCreator.js'

class BaseSchemaDrawer {

  _markers = {};
  _tkoObjects = [];
  _garbageSources = [];
  _map = null;
  _tkoCluster       = null;
  _removedPolylines = {};
  _cities = [];
  _unauthorizedHeaps = [];
  _msnContainers = [];
  showMarkerSubs = null;
  vm = null;
  $emitter = null;
  // popupCreator = null
  // markerCreator = null

  /**
   * @param map
   * @param vm{Vue}
   */
  constructor (map, vm) {
    this._map = map;
    this.vm = vm;
    this.popupCreator = new PopupCreator(vm);
    this.markerCreator = new MarkerCreator(vm);
    this.initObservers()
  }

  initObservers () {
    this.$emitter = this.vm.$emitter
  }

  removeAllObjects () {
    for (let key in this._markers) {
      if (this._markers[key]) {
        this._markers[key].remove();
        this._markers[key] = null
      }
    }

    if (this._tkoCluster) {
      this._tkoCluster.remove();
      this._tkoCluster = null
    }

    if (this._geoJson) {
      this._geoJson.remove()
    }
  }

  redrawAll () {
    this.removeAllObjects();
    this.drawCities();
    this.drawTkoObjects();
    this.drawRoute();
    this.drawGeoJson()
  }

  refreshMap (cities, tko) {
    this.removeAllObjects();
    this.setCities(cities);
    this.setTkoObjects(tko);
    if (this.$emitter) {
      this.$emitter.emit('baseThreadsDrawer.refreshed', {})
    }
  }

  destroy () {
    this.disposeObservers()
  }

  disposeObservers () {
    this.showMarkerSubs.unsubscribe()
  }

  setGeoJson (geoJson, draw = true) {
    if (this._geoJson) {
      this._geoJson.remove()
    }
    this._geoJsonData = this.dropVueWatchers(geoJson);
    if(draw){
      this.drawGeoJson()
    }
  }

  drawGeoJson () {
    this._geoJson = L.geoJson(this._geoJsonData).addTo(this._map);
    if (this._geoJson && this._geoJson._layers) {
      let bounds = this._geoJson.getBounds();
      this._map.fitBounds(bounds)
      // this._map.flyToBounds(bounds)
    }
  }

  setRoute (geoJson, draw = true) {
    geoJson = this.dropVueWatchers(geoJson);
    this._routeWaypoints = geoJson;
    if(draw){
      this.drawRoute()
    }
  }

  drawRoute () {
    let tmp = [];
    this._routeWaypoints.forEach(v => {
      tmp.push([v[1], v[0]])
    });
    if (this._route) {
      this._route.remove()
    }
    this._route = L.polyline(tmp, {
      color: '#4389fc'
    }).addTo(this._map)
  }

  setTkoObjects (tkoObjects, draw = true) {
    if(tkoObjects) {
      this._tkoObjects = Object.values(tkoObjects);
    }
    if(draw){
      this.drawTkoObjects()
    }
  }

  setCities (cities, draw = true) {
    this._cities = this.dropVueWatchers(cities || []);
    if(draw){
      this.drawCities()
    }

    // this.drawHeatMapOverlay()
  }

  drawCities () {
    this.updateZoom();
    this._cities.forEach(v => {
      if (this.isCanShowOnThatZoom(v)) {
        this.drawIfNotExist(this.getOrCreateMarker(v))
      } else {
        this.removeMarkerIfExist(v)
      }
    })
  }

  setGarbageSources (garbageSources, draw = false) {
    this._garbageSources = garbageSources || [];
    if(draw){
      this.drawGarbageSources()
    }
    // this.drawHeatMapOverlay()
  }

  setUnauthorizedTkos (unauthorizedTkos, draw = true) {
    this._unauthorizedTkos = this.dropVueWatchers(unauthorizedTkos || []);
    if(draw){
      this.drawUnauthorizedTkos()
    }
    // this.drawHeatMapOverlay()
  }

  setUnauthorizedHeaps (unauthorizedHeaps, draw = true) {
    this._unauthorizedHeaps = this.dropVueWatchers(unauthorizedHeaps || []);
    if(draw){
      this.drawUnauthorizedHeaps()
    }
    // this.drawHeatMapOverlay()
  }

  setMSNContainers (msnContainers, draw = true) {
    this._msnContainers = this.dropVueWatchers(msnContainers || []);
    if(draw){
      this.drawMSNContainers()
    }
    // this.drawHeatMapOverlay()
  }

  drawGarbageSources () {
    this.updateZoom();
    this._garbageSources.forEach(v => {
      if (this.isCanShowOnThatZoom(v)) {
        this.drawIfNotExist(this.getOrCreateMarker(v))
      } else {
        this.removeMarkerIfExist(v)
      }
    })
  }

  drawUnauthorizedTkos () {
    this.updateZoom();
    this._unauthorizedTkos.forEach(v => {
      if (this.isCanShowOnThatZoom(v)) {
        this.drawIfNotExist(this.getOrCreateMarker(v))
      } else {
        this.removeMarkerIfExist(v)
      }
    })
  }

  drawUnauthorizedHeaps () {
    this.updateZoom();
    this._unauthorizedHeaps.forEach(v => {
      if (this.isCanShowOnThatZoom(v)) {
        this.drawIfNotExist(this.getOrCreateMarker(v))
      } else {
        this.removeMarkerIfExist(v)
      }
    })
  }

  drawMSNContainers () {
    this.updateZoom();
    this._msnContainers.forEach(v => {
      if (this.isCanShowOnThatZoom(v)) {
        this.drawIfNotExist(this.getOrCreateMarker(v))
      } else {
        this.removeMarkerIfExist(v)
      }
    })
  }

  createClusterIfNotExist () {
    if (!this._tkoCluster) {
      this._tkoCluster = L.markerClusterGroup({
        iconCreateFunction: (cluster) => L.divIcon({
          className: 'cluster-marker object',
          html: cluster.getChildCount(),
          iconSize: [36, 36],
          iconAnchor: [18, 18]
        }),
        showCoverageOnHover: false,
        markersInLayer: []
      });
      // @fix: #12429
      this._tkoCluster.on('spiderfied', function (a) {
        if (a.cluster && a.cluster.options && a.cluster.options.icon) {
          a.cluster.options.icon.setOpacity(0)
        }
      });
      this._map.addLayer(this._tkoCluster)
    }
  }

  dropVueWatchers (obj) {
    return JSON.parse(JSON.stringify(obj))
  }

  updateZoom() {
    this._zoom = this._map.getZoom()
  }

  drawTkoObjects () {
    this.updateZoom();
  
    let openedPopupInfo = this.vm.$route.query.openedPopupInfo;
    for (const v of this._tkoObjects) {
      let marker = this.getOrCreateMarker(v);
      marker.on('click', () => {
        this.createPopup(marker)
      });
      this.drawIfNotExist(marker);
    
      if (openedPopupInfo && marker.options.openedPopupInfo === openedPopupInfo) {
        this.createPopup(marker)
      }
    }
  }
  
  isCanShowOnThatZoom (obj) {
    if (this.vm.$route.query.openedPopupInfo) {
      let id = this.getMarketId(obj);
      if (this.vm.$route.query.openedPopupInfo && id === this.vm.$route.query.openedPopupInfo) {
        return true
      }
    }
    
    return this._zoom > obj.showAfterZoom || (process.env.REGION === 'primor' && obj?.type === OBJECT.TKO)
  }

  removeMarkerIfExist (obj) {
    let id = this.getMarketId(obj);
    if (this._markers[id]) {
      this._markers[id].remove();
      this._markers[id] = null
    }
  }

  getMarketId (obj) {
    if (obj.type === OBJECT.REL) {
      return obj.start.id + '_' + obj.end.id + '_' + obj.type + '_' + obj?.relationType
    }
    return obj.id + obj.type
  }

  drawIfNotExist (marker) {
    if (!marker._map) {
      marker.addTo(this._map);
      marker.on('click', () => this.createPopup(marker))
    }

    if (marker.options.openedPopupInfo === this.vm.$route.query.openedPopupInfo) {
      this.createPopup(marker)
    }

    return marker
  }

  createPopup (marker, options = {}) {
    let {forceReDraw} = options;
    // fix: opened relation edit form
    if (forceReDraw && marker._popup) {
      marker._popup.remove();
      marker._popup = null
    }
    let popup = null;
    let popupOptions = options;
    if(marker && marker.options && marker.options.popupOptions) {
      popupOptions = marker.options.popupOptions;
    }
    if (!marker._popup) {
      let popupId = this.getMarketId(marker.options.model);
      popup = this.popupCreator.getPopup(marker, marker.options.model.type, popupId, popupOptions);
      if (popup) {
        marker.bindPopup(popup).openPopup();
        this.vm.registerOpenPopup(popup)
      }
    } else {
      this.vm.registerOpenPopup(marker._popup);
      popup = marker._popup
    }

    if (!popupOptions.autoPan) {
      this.punPopup(marker, popup)
    }
    return popup
  }

  /**
   * if popup bounds leave container bound
   * set new map center
   * @param marker
   * @param popup
   */
  punPopup (marker, popup) {
    setTimeout(() => {
      if (marker && marker._map && popup) {
        let point = marker._map.latLngToLayerPoint(popup.getLatLng());
        let rectMap = marker._map._container.getBoundingClientRect();
        let rectPopup = popup._container.getBoundingClientRect();
        let needChangeMapCenter = false;
        // top margin
        if (rectPopup.top < rectMap.top) {
          // point.y = point.y + size
          needChangeMapCenter = true
        }
        // left margin
        if (rectPopup.left < rectMap.left) {
          // point.x = point.x + size
          needChangeMapCenter = true
        }

        if (needChangeMapCenter === true) {
          let latlng = marker._map.layerPointToLatLng(point);
          marker._map.setView(latlng)
        }
      } else {
        console.error('data for punPopup not found', marker, popup)
      }
    }, 0)
  }

  getOrCreateMarker (obj, options = {}) {
    let id = this.getMarketId(obj);
    if (!this._markers[id]) {
      if (!obj.lat || !obj.lng) {
        console.log('Unknow lat, lng for ' + obj.type + ' = ' + JSON.stringify(obj))
      }
      this._markers[id] = this.markerCreator.getMarker(obj, obj.type, id, options);
      if (obj.tooltip || obj.name) {
        this._markers[id].bindTooltip(
          obj.tooltip || obj.name,
          {
          //   ...((process.env.REGION === 'primor' || process.env.REGION === 'len') && obj?.type === OBJECT.TKO) && {
          //   permanent: true, direction: 'bottom'
          // }
          }
        )
      }
      if (obj.type === OBJECT.CITY) {
        this._markers[id].bindTooltip(obj.name)
      }
    }
    return this._markers[id]
  }
}

export default BaseSchemaDrawer
